import { subscribe } from './pubsub';
import { PUB_SUB_EVENTS } from './constants';

class CustomQuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.minusButton = this.querySelector('button[name="minus"]');
    this.plusButton = this.querySelector('button[name="plus"]');
    this.changeEvent = new Event('change', { bubbles: true });

    this.setupAccessibility();
    this.setupEventListeners();
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.updateAriaAttributes();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  setupAccessibility() {
    if (this.input) {
      // Add live region for announcements
      this.createLiveRegion();
    }
  }

  setupEventListeners() {
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.input.addEventListener('keydown', this.onInputKeydown.bind(this));

    this.querySelectorAll('button').forEach((button) => {
      button.addEventListener('click', this.onButtonClick.bind(this));
      button.addEventListener('keydown', this.onButtonKeydown.bind(this));
    });
  }

  createLiveRegion() {
    // Create a live region for screen reader announcements
    this.liveRegion = document.createElement('div');
    this.liveRegion.setAttribute('aria-live', 'polite');
    this.liveRegion.setAttribute('aria-atomic', 'true');
    this.liveRegion.className = 'sr-only';
    this.appendChild(this.liveRegion);
  }

  announceToScreenReader(message: string) {
    if (this.liveRegion) {
      this.liveRegion.textContent = message;
    }
  }

  updateAriaAttributes() {
    if (this.input) {
      // Update button states
      this.updateButtonAccessibility();
    }
  }

  updateButtonAccessibility() {
    const currentValue = parseInt(this.input.value) || 0;
    const min = parseInt(this.input.min) || 0;
    const max = parseInt(this.input.max) || 999;

    if (this.minusButton) {
      const isDisabled = currentValue <= min;
      this.minusButton.setAttribute('aria-disabled', isDisabled.toString());

      if (isDisabled) {
        this.minusButton.setAttribute('tabindex', '-1');
      } else {
        this.minusButton.setAttribute('tabindex', '0');
      }
    }

    if (this.plusButton) {
      const isDisabled = currentValue >= max;
      this.plusButton.setAttribute('aria-disabled', isDisabled.toString());

      if (isDisabled) {
        this.plusButton.setAttribute('tabindex', '-1');
      } else {
        this.plusButton.setAttribute('tabindex', '0');
      }
    }
  }

  onInputChange(_event: Event) {
    this.validateQtyRules();
    this.updateAriaAttributes();
  }

  onInputKeydown(event: KeyboardEvent) {
    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault();
        this.increaseQuantity();
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.decreaseQuantity();
        break;
      case 'Home':
        event.preventDefault();
        this.setToMinimum();
        break;
      case 'End':
        event.preventDefault();
        this.setToMaximum();
        break;
    }
  }

  onButtonKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onButtonClick(event);
    }
  }

  onButtonClick(event: Event) {
    event.preventDefault();
    const previousValue = this.input.value;
    const button = event.currentTarget as HTMLButtonElement;

    if (button.name === 'plus') {
      this.increaseQuantity();
    } else {
      this.decreaseQuantity();
    }

    if (previousValue !== this.input.value) {
      this.input.dispatchEvent(this.changeEvent);
    }

    // Update accessibility attributes
    this.updateAriaAttributes();
  }

  increaseQuantity() {
    const currentValue = parseInt(this.input.value) || 0;
    const min = parseInt(this.input.dataset.min) || parseInt(this.input.min) || 0;
    const step = parseInt(this.input.step) || 1;

    if (currentValue === 0 && min > step) {
      this.input.value = min.toString();
    } else {
      this.input.stepUp();
    }
  }

  decreaseQuantity() {
    const currentValue = parseInt(this.input.value) || 0;
    const min = parseInt(this.input.min) || 0;

    if (currentValue > min) {
      this.input.stepDown();
    }
  }

  setToMinimum() {
    const min = parseInt(this.input.min) || 0;
    this.input.value = min.toString();
    this.input.dispatchEvent(this.changeEvent);
    this.announceToScreenReader(`Quantity set to minimum: ${min}`);
  }

  setToMaximum() {
    const max = parseInt(this.input.max) || 999;
    this.input.value = max.toString();
    this.input.dispatchEvent(this.changeEvent);
    this.announceToScreenReader(`Quantity set to maximum: ${max}`);
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);

    if (this.input.min) {
      const min = parseInt(this.input.min);
      const buttonMinus = this.querySelector(".quantity__button[name='minus']") as HTMLButtonElement;
      if (buttonMinus) {
        const isDisabled = value <= min;
        buttonMinus.classList.toggle('disabled', isDisabled);
        buttonMinus.setAttribute('aria-disabled', isDisabled.toString());
        buttonMinus.setAttribute('tabindex', isDisabled ? '-1' : '0');
      }
    }

    const buttonPlus = this.querySelector(".quantity__button[name='plus']") as HTMLButtonElement;
    if (buttonPlus) {
      if (this.input.max) {
        const max = parseInt(this.input.max);
        const isDisabled = value >= max;
        buttonPlus.classList.toggle('disabled', isDisabled);
        buttonPlus.setAttribute('aria-disabled', isDisabled.toString());
        buttonPlus.setAttribute('tabindex', isDisabled ? '-1' : '0');
      } else {
        buttonPlus.classList.remove('disabled');
        buttonPlus.setAttribute('aria-disabled', 'false');
        buttonPlus.setAttribute('tabindex', '0');
      }
    }

    // Update ARIA attributes after validation
    this.updateAriaAttributes();
  }
}

if (!customElements.get('custom-quantity-input')) {
  customElements.define('custom-quantity-input', CustomQuantityInput);
}
